import React from 'react';
import { Calendar as CalendarIcon, AlertCircle, RefreshCw } from 'lucide-react';
import { CalendarMonth } from './CalendarMonth';
import { useTemplateCalendar, useSyncTemplateCalendar } from '@/hooks/schedules/useTemplateCalendar';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/loading-states';
import { QueryError } from '@/components/ui/error-state';
import type { ScheduleTemplate, CalendarDay } from '@/types/schedule.types';

interface ScheduleCalendarProps {
  selectedTemplate: ScheduleTemplate | null;
  onDayClick?: (day: CalendarDay) => void;
  selectedDays?: string[];
  onDaySelect?: (dayIds: string[]) => void;
  onSelectedDaysChange?: (days: CalendarDay[]) => void;
  onBulkEdit?: () => void;
  onBulkUpdate?: (dayIds: string[], updates: Partial<CalendarDay>) => Promise<void>;
}

export const ScheduleCalendar: React.FC<ScheduleCalendarProps> = ({
  selectedTemplate,
  onDayClick,
  selectedDays = [],
  onDaySelect,
  onSelectedDaysChange,
  onBulkEdit,
  onBulkUpdate,
}) => {
  const [currentYear, setCurrentYear] = React.useState(new Date().getFullYear());
  const [currentMonth, setCurrentMonth] = React.useState(new Date().getMonth() + 1);

  const { data, isLoading, error, refetch } = useTemplateCalendar(
    selectedTemplate?.id || null,
    currentYear,
    currentMonth
  );

  const syncMutation = useSyncTemplateCalendar();

  // Update selected days data when selection changes
  React.useEffect(() => {
    if (data?.calendar && selectedDays.length > 0) {
      const selectedCalendarDays = data.calendar.filter(day =>
        selectedDays.includes(day.id)
      );
      onSelectedDaysChange?.(selectedCalendarDays);
    } else {
      onSelectedDaysChange?.([]);
    }
  }, [selectedDays, data?.calendar, onSelectedDaysChange]);

  const handleNavigate = (year: number, month: number) => {
    setCurrentYear(year);
    setCurrentMonth(month);
  };

  const handleSync = async () => {
    if (selectedTemplate?.id) {
      try {
        await syncMutation.mutateAsync(selectedTemplate.id);
        refetch();
      } catch (error) {
        console.error('Sync failed:', error);
      }
    }
  };

  if (!selectedTemplate) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center">
          <CalendarIcon className="mx-auto h-16 w-16 text-muted-foreground/50" />
          <h3 className="mt-4 text-lg font-medium">Выберите шаблон графика</h3>
          <p className="mt-2 text-sm text-muted-foreground max-w-sm">
            Выберите шаблон из списка слева, чтобы просмотреть его календарь
          </p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="mb-6">
          <div className="h-6 w-48 bg-gray-200 rounded animate-pulse mb-2" />
          <div className="h-4 w-32 bg-gray-100 rounded animate-pulse" />
        </div>
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <QueryError
          error={error}
          refetch={refetch}
        />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center">
          <AlertCircle className="mx-auto h-16 w-16 text-muted-foreground/50" />
          <h3 className="mt-4 text-lg font-medium">Данные календаря недоступны</h3>
          <p className="mt-2 text-sm text-muted-foreground max-w-sm">
            Для этого шаблона отсутствуют календарные данные
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {data.is_generated && selectedTemplate.is_1c_imported && (
        <div className="border-b bg-amber-50 dark:bg-amber-900/20 p-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-amber-600" />
              <span className="text-sm text-amber-700 dark:text-amber-300">
                Календарь сгенерирован из шаблона. Для получения точных данных синхронизируйтесь с 1С.
              </span>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={handleSync}
              disabled={syncMutation.isPending}
            >
              {syncMutation.isPending ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-2" />
              )}
              Синхронизировать
            </Button>
          </div>
        </div>
      )}

      <div className="flex-1 overflow-hidden">
        <CalendarMonth
          year={currentYear}
          month={currentMonth}
          calendarDays={data.calendar || []}
          onNavigate={handleNavigate}
          onDayClick={onDayClick}
          onDayUpdate={async (dayId: string, updates: Partial<CalendarDay>) => {
            if (onBulkUpdate) {
              await onBulkUpdate([dayId], updates);
              refetch(); // Refresh data after update
            }
          }}
          onBulkUpdate={async (dayIds: string[], updates: Partial<CalendarDay>) => {
            if (onBulkUpdate) {
              await onBulkUpdate(dayIds, updates);
              refetch(); // Refresh data after update
            }
          }}
          selectedDays={selectedDays}
          onDaySelect={onDaySelect}
          onBulkEdit={onBulkEdit}
          templateName={selectedTemplate.name}
          isGenerated={data.is_generated}
        />
      </div>
    </div>
  );
};